import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    FormControlLabel,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import {
    getOffsiteCampaignDetails,
    getWalletDashboard,
    markAllCampaignThreadsRead,
    updateCampaignStatus,
} from '../../redux/buzzbridge/actions';
import {
    currentCampaignSelector,
    gettingCampaignDetailsSelector,
    updatingCampaignStatusSelector,
} from '../../redux/buzzbridge/selectors';
import { BRAND_LUXE_PRIMARY, BRAND_LUXE_SECONDARY, BRAND_SURFACE_CONTAINER_LOW } from './brandDashboardTokens';
import { midnight } from './contestWizard/midnightStyles';
import { RichTextViewer } from '../../components/Common/RichTextViewer';
import { CampaignOverviewCards } from './campaignDetail/CampaignOverviewCards';
import { CampaignParticipants } from './campaignDetail/CampaignParticipants';
import { CampaignDetailSections } from './campaignDetail/CampaignDetailSections';
import CloseIcon from '@mui/icons-material/Close';
import { CampaignManageGrid, type CampaignManagePanel } from './campaignDetail/CampaignManageGrid';
import { CampaignBriefPanel } from './campaignDetail/CampaignBriefPanel';
import { CampaignDeliverablesPanel } from './campaignDetail/CampaignDeliverablesPanel';
import { CampaignTargetingPanel } from './campaignDetail/CampaignTargetingPanel';
import { CampaignAssetsPanel } from './campaignDetail/CampaignAssetsPanel';
import { CampaignSampleVideosPanel } from './campaignDetail/CampaignSampleVideosPanel';
import { CampaignAddCreatorsPanel } from './campaignDetail/CampaignAddCreatorsPanel';
import { ResourceTeamPanel } from '../../components/BrandTeam/ResourceTeamPanel';
import CampaignEditModal from './campaignDetail/CampaignEditModal';
import CampaignFundModal from './campaignDetail/CampaignFundModal';
import { BrandCampaignSubscriptionGuard } from './subscription/BrandCampaignSubscriptionGuard';
import { CampaignHostTierPickerModal } from './subscription/CampaignHostTierPickerModal';
import { CampaignHostCheckoutDialog } from './subscription/CampaignHostCheckoutDialog';
import { useCampaignHostCheckout } from './subscription/useCampaignHostCheckout';
import {
    hasActiveCampaignHostPlanSelector,
    // campaignHostPackagesSelector,
    // campaignHostLtdPromoActiveSelector,
    campaignHostPricingSelector,
} from '../../redux/campaignHostSubscription/selectors';
import { fetchCampaignHostPricing } from '../../redux/campaignHostSubscription/actions';
import type { ICampaignHostPackage } from '../../types/global/campaignHostSubscription.types';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { showToastMessage } from '../../utils/AppUtils';
import { marketingAppInstallDocsUrl } from '../../utils/marketingSiteUrls';
import { CampaignThreadUnreadBadge } from '../../components/BuzzBridge/CampaignThreadUnreadBadge';
import { normalizeUnreadCount } from '../../utils/campaignThreadUnread';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { DuplicateCampaignDialog, isUgcCampaignDuplicable } from './DuplicateCampaignDialog';
import { Tutorial } from '../../components/Tutorial/Tutorial';
import { campaignDetailTutorialSteps } from '../../components/EsSettings/settings.data';
import { showCampaignDetailGuideSelector } from '../../selectors/selectors';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { closeUgcCampaign } from '../../components/UgcCampaignsTab/ugcBoardApi';
import type { RootState } from '../../redux/root-reducer';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const MIN_ESCROW_LOCK_CENTS = 100;

export default function BrandCampaignDetailPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { campaignId } = useParams<{ campaignId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    const campaign = useSelector(currentCampaignSelector);
    const loading = useSelector(gettingCampaignDetailsSelector);
    const updating = useSelector(updatingCampaignStatusSelector);
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const hostPricing = useSelector(campaignHostPricingSelector);
    const perCampaignFeeCents = hostPricing?.per_campaign_fee_cents ?? 5000;
    const authToken = useSelector((s: RootState) => s.authentication.token);

    const [editOpen, setEditOpen] = useState(false);
    const [fundModalOpen, setFundModalOpen] = useState(false);
    const [activePanel, setActivePanel] = useState<CampaignManagePanel | null>(null);
    const showDetailTour = useSelector(showCampaignDetailGuideSelector);
    const [subGuardOpen, setSubGuardOpen] = useState(false);
    const [tierPickerOpen, setTierPickerOpen] = useState(false);
    const autoFundPromptHandled = useRef(false);

    // Action menu
    const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);

    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
    const [closingCampaign, setClosingCampaign] = useState(false);
    const canDuplicate = campaign ? isUgcCampaignDuplicable(campaign) : false;
    const canCloseCampaign = !!campaign && (campaign.status === 'published' || campaign.status === 'paused');

    const openFundModal = useCallback(
        (opts?: { skipSubscriptionGuard?: boolean }) => {
            if (!opts?.skipSubscriptionGuard && !hasPlan) {
                dispatch(fetchCampaignHostPricing());
                setSubGuardOpen(true);
                return;
            }
            dispatch(getWalletDashboard());
            setFundModalOpen(true);
        },
        [dispatch, hasPlan],
    );

    const { checkoutOpen, setCheckoutOpen, beginMonthly, beginLtd, onPaymentMethodReady, checkoutTitle } =
        useCampaignHostCheckout({
            onSubscriptionComplete: () => {
                setTierPickerOpen(false);
                setSubGuardOpen(false);
                dispatch(getWalletDashboard());
                setFundModalOpen(true);
            },
        });

    const handleSelectMonthly = useCallback(
        (pkg: ICampaignHostPackage) => {
            setTierPickerOpen(false);
            beginMonthly(pkg);
        },
        [beginMonthly],
    );

    const handleSelectLtd = useCallback(
        (pkg: ICampaignHostPackage) => {
            setTierPickerOpen(false);
            beginLtd(pkg);
        },
        [beginLtd],
    );

    useEffect(() => {
        if (campaignId) {
            dispatch(getOffsiteCampaignDetails({ campaignId, asBrandHost: true }));
        }
    }, [campaignId, dispatch]);

    useEffect(() => {
        dispatch(fetchCampaignHostPricing());
    }, [dispatch]);

    const isPublished = campaign?.status === 'published';
    const isFunded = campaign?.funded === true;
    const canCopyCampaignLink = isFunded && isPublished;
    const campaignAccess = (
        campaign as { access?: { can_view_prices?: boolean; can_use_wallet?: boolean; can_publish?: boolean } } | null
    )?.access;
    const hideMoney = campaignAccess?.can_view_prices === false;
    const canPublishCampaign = campaignAccess ? campaignAccess.can_publish !== false : true;
    const canUseWallet = campaignAccess ? campaignAccess.can_use_wallet === true : true;
    const budgetCents = hideMoney ? 0 : Math.round((Number(campaign?.total_budget) || 0) * 100);
    const isAppInstallCpa = Boolean(campaign?.app_install?.enabled);
    const isFundingPaused = campaign?.status === 'paused' || campaign?.funding_paused === true;
    // Commission campaigns must lock escrow before publishing.
    const isPercentCommission = campaign?.campaign_type === 'commission' && !isAppInstallCpa;
    const isExternalCommission = isPercentCommission && (campaign as any)?.commission?.target_type === 'external';
    const commissionBaseCents = Math.round(Number((campaign as any)?.base_payment_per_creator_cents) || 0);
    const commissionSeats = Math.max(1, Number(campaign?.total_participants_count) || 1);
    const commissionBasePayObligation = isPercentCommission ? commissionBaseCents * commissionSeats : 0;
    const externalPoolCents = isExternalCommission
        ? Math.round(Number((campaign as any)?.commission?.display_pool_cents) || 0)
        : 0;
    // For external commission: needs escrow for pool + base pay while unfunded.
    // For non-external commission with base pay: needs escrow while unfunded.
    const commissionNeedsEscrowFund = isExternalCommission
        ? externalPoolCents + commissionBasePayObligation > 0 && !isPublished && !isFunded
        : isPercentCommission && commissionBasePayObligation > 0 && !isPublished && !isFunded;
    /** Unfunded campaigns, or paused app-install CPA that need a float top-up to restart. */
    const canShowFundButton =
        !!campaign &&
        (commissionNeedsEscrowFund ||
            (budgetCents >= MIN_ESCROW_LOCK_CENTS &&
                ((!isFunded && !isFundingPaused) || (isAppInstallCpa && isFundingPaused))));
    const fundButtonLabel =
        isAppInstallCpa && isFundingPaused
            ? 'Add funds'
            : isExternalCommission && commissionNeedsEscrowFund
            ? 'Lock commission escrow'
            : commissionNeedsEscrowFund
            ? 'Lock base pay'
            : 'Fund campaign';

    useEffect(() => {
        if (autoFundPromptHandled.current) return;
        if (loading || !campaign) return;
        if (searchParams.get('fund') !== '1') return;
        const allowAutoFund =
            (!isFunded && budgetCents >= MIN_ESCROW_LOCK_CENTS) ||
            (isAppInstallCpa && isFundingPaused && budgetCents >= MIN_ESCROW_LOCK_CENTS);
        if (!allowAutoFund) {
            autoFundPromptHandled.current = true;
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.delete('fund');
                    return next;
                },
                { replace: true },
            );
            return;
        }

        autoFundPromptHandled.current = true;
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                next.delete('fund');
                return next;
            },
            { replace: true },
        );
        openFundModal({ skipSubscriptionGuard: true });
    }, [
        loading,
        campaign,
        searchParams,
        isFunded,
        isAppInstallCpa,
        isFundingPaused,
        budgetCents,
        openFundModal,
        setSearchParams,
    ]);

    const handlePublishToggle = () => {
        if (!campaign || !isFunded) return;
        if (commissionNeedsEscrowFund) {
            openFundModal({ skipSubscriptionGuard: true });
            return;
        }
        const newStatus = isPublished ? 'draft' : 'published';
        dispatch(updateCampaignStatus({ campaignId: campaign._id, status: newStatus }));
    };

    const publicCampaignUrl = campaignId
        ? `${window.location.origin}${replaceRouteParam(ROUTES.UGC_CAMPAIGN_DETAIL, 'campaignId', campaignId)}`
        : '';

    const handleCopyCampaignLink = useCallback(async () => {
        if (!publicCampaignUrl) return;
        try {
            await navigator.clipboard.writeText(publicCampaignUrl);
            showToastMessage('Campaign link copied', 'success');
        } catch {
            showToastMessage('Could not copy link', 'error');
        }
    }, [publicCampaignUrl]);

    const unreadMessagesTotal = normalizeUnreadCount(campaign?.unread_messages_total);

    const handleMarkAllMessagesRead = useCallback(() => {
        if (!campaignId || unreadMessagesTotal <= 0) return;
        dispatch(markAllCampaignThreadsRead({ campaignId }));
    }, [campaignId, dispatch, unreadMessagesTotal]);

    if (loading || !campaign) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    const participants = campaign.participants ?? [];
    const pendingRequests = campaign.pending_requests ?? [];
    const pendingInvites = campaign.pending_invites ?? [];

    /** Same highlight as UGC discovery “Join campaign” / Follow (`primary_orange` + dark text) */
    const highlightButtonSx = {
        py: 1.25,
        borderRadius: 999,
        textTransform: 'none' as const,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: 13,
        bgcolor: t.primary_orange,
        border: `1px solid ${t.primary_orange}`,
        color: '#131517',
        '&:hover': { bgcolor: t.primary_orange },
        '& .MuiButton-startIcon': { color: '#131517' },
    };

    return (
        <MetaTags title={`${campaign.title} | Campaigns`} description="Campaign detail">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{ alignSelf: 'flex-start', '& .MuiBreadcrumbs-separator': { color: t.form_label } }}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.BRAND_CAMPAIGNS}
                        underline="hover"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: t.primary_white,
                            '&:hover': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        Campaigns
                    </Link>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                        Campaigns
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(ROUTES.BRAND_CAMPAIGNS)}
                            sx={{
                                color: BRAND_LUXE_PRIMARY,
                                bgcolor: 'rgba(255,201,101,0.08)',
                                '&:hover': { bgcolor: 'rgba(255,201,101,0.15)' },
                            }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: 22, md: 28 },
                                        color: '#fff',
                                        letterSpacing: '-0.02em',
                                    }}>
                                    {campaign.title}
                                </Typography>
                                <StatusChip status={campaign.status} />
                                {unreadMessagesTotal > 0 && (
                                    <CampaignThreadUnreadBadge count={unreadMessagesTotal} size="md" />
                                )}
                                {isFunded && (
                                    <Chip
                                        label="Funded"
                                        size="small"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 11,
                                            height: 24,
                                            bgcolor: `${BRAND_LUXE_SECONDARY}18`,
                                            color: BRAND_LUXE_SECONDARY,
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: 'rgba(255,255,255,0.5)',
                                }}>
                                Created{' '}
                                {new Date(campaign.createdAt!).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                })}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        id="campaign-detail-actions"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                        }}>
                        {canShowFundButton && canUseWallet && (
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <Tooltip
                                    title={
                                        isAppInstallCpa && isFundingPaused
                                            ? 'Add escrow funds to refill install budget and restart this campaign'
                                            : commissionNeedsEscrowFund
                                            ? `Lock $${(commissionBasePayObligation / 100).toFixed(
                                                  2,
                                              )} base pay in escrow before publishing`
                                            : 'Lock your campaign budget from your campaign wallet into escrow'
                                    }>
                                    <Button
                                        startIcon={<AccountBalanceWalletOutlinedIcon />}
                                        onClick={() => openFundModal()}
                                        sx={{ px: 3, ...highlightButtonSx }}>
                                        {fundButtonLabel}
                                    </Button>
                                </Tooltip>
                            </Box>
                        )}
                        {/* Publish switch */}
                        <Tooltip
                            title={
                                !isFunded
                                    ? 'Campaign must be funded before publishing'
                                    : commissionNeedsEscrowFund
                                    ? `Lock base pay ($${(commissionBasePayObligation / 100).toFixed(
                                          2,
                                      )}) in escrow before publishing`
                                    : isPublished
                                    ? 'Unpublish campaign'
                                    : 'Publish campaign'
                            }>
                            {updating ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            color: 'rgba(255,255,255,0.5)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}>
                                        {isPublished ? 'Published' : 'Draft'}
                                    </Typography>
                                    <CircularProgress size={18} sx={{ color: BRAND_LUXE_SECONDARY }} />
                                </Box>
                            ) : (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isPublished}
                                            onChange={handlePublishToggle}
                                            disabled={!isFunded || !canPublishCampaign}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_SECONDARY },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    bgcolor: BRAND_LUXE_SECONDARY + '66',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 12,
                                                color: isPublished ? BRAND_LUXE_SECONDARY : 'rgba(255,255,255,0.5)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.08em',
                                            }}>
                                            {isPublished ? 'Published' : 'Draft'}
                                        </Typography>
                                    }
                                    labelPlacement="start"
                                    id="campaign-publish-toggle"
                                    sx={{ mr: 0, gap: 1 }}
                                />
                            )}
                        </Tooltip>

                        {unreadMessagesTotal > 0 && (
                            <Button
                                startIcon={<MarkEmailReadOutlinedIcon />}
                                onClick={handleMarkAllMessagesRead}
                                sx={{ px: 3, ...highlightButtonSx }}>
                                Mark all read ({unreadMessagesTotal})
                            </Button>
                        )}

                        {/* Action menu */}
                        <Tooltip title="More actions">
                            <IconButton
                                onClick={e => setActionMenuAnchor(e.currentTarget)}
                                sx={{
                                    color: BRAND_LUXE_PRIMARY,
                                    bgcolor: 'rgba(255,201,101,0.08)',
                                    '&:hover': { bgcolor: 'rgba(255,201,101,0.15)' },
                                }}>
                                <MoreVertIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor)}
                            onClose={() => setActionMenuAnchor(null)}
                            PaperProps={{
                                sx: {
                                    bgcolor: '#1e1f24',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 2,
                                    minWidth: 180,
                                },
                            }}>
                            <MenuItem
                                disabled={!canCopyCampaignLink}
                                onClick={() => {
                                    setActionMenuAnchor(null);
                                    handleCopyCampaignLink();
                                }}
                                sx={{
                                    gap: 1.5,
                                    color: canCopyCampaignLink ? '#fff' : 'rgba(255,255,255,0.3)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                }}>
                                <LinkOutlinedIcon fontSize="small" />
                                Copy link
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setActionMenuAnchor(null);
                                    navigate(ROUTES.BRAND_CAMPAIGN_REPORT.replace(':campaignId', campaignId!));
                                }}
                                sx={{ gap: 1.5, color: '#fff', fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14 }}>
                                <InsightsOutlinedIcon fontSize="small" />
                                Report
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setActionMenuAnchor(null);
                                    setEditOpen(true);
                                }}
                                sx={{ gap: 1.5, color: '#fff', fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14 }}>
                                <EditOutlinedIcon fontSize="small" />
                                Edit
                            </MenuItem>
                            <MenuItem
                                disabled={!canDuplicate}
                                onClick={() => {
                                    setActionMenuAnchor(null);
                                    setDuplicateOpen(true);
                                }}
                                sx={{
                                    gap: 1.5,
                                    color: canDuplicate ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.3)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                }}>
                                <ContentCopyOutlinedIcon fontSize="small" />
                                Duplicate
                            </MenuItem>
                            <MenuItem
                                disabled={!canCloseCampaign || closingCampaign}
                                onClick={() => {
                                    setActionMenuAnchor(null);
                                    setCloseConfirmOpen(true);
                                }}
                                sx={{
                                    gap: 1.5,
                                    color: canCloseCampaign ? '#ff6b6b' : 'rgba(255,255,255,0.3)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                }}>
                                <LockOutlinedIcon fontSize="small" />
                                Close campaign
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                {canShowFundButton && canUseWallet && (
                    <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
                        <Tooltip
                            title={
                                isAppInstallCpa && isFundingPaused
                                    ? 'Add escrow funds to refill install budget and restart this campaign'
                                    : commissionNeedsEscrowFund
                                    ? `Lock $${(commissionBasePayObligation / 100).toFixed(
                                          2,
                                      )} base pay in escrow before publishing`
                                    : 'Lock your campaign budget from your campaign wallet into escrow'
                            }>
                            <Button
                                fullWidth
                                startIcon={<AccountBalanceWalletOutlinedIcon />}
                                onClick={() => openFundModal()}
                                sx={highlightButtonSx}>
                                {fundButtonLabel}
                            </Button>
                        </Tooltip>
                    </Box>
                )}

                {/* Feature Image Banner */}
                {campaign.feature_image ? (
                    <Box
                        sx={{ height: 200, borderRadius: 3, overflow: 'hidden', bgcolor: BRAND_SURFACE_CONTAINER_LOW }}>
                        <Box
                            component="img"
                            src={campaign.feature_image}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: 120,
                            borderRadius: 3,
                            overflow: 'hidden',
                            background: `linear-gradient(135deg, rgba(255,201,101,0.12) 0%, rgba(0,253,193,0.06) 100%)`,
                            border: `1px solid rgba(255,201,101,0.08)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <RocketLaunchOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,201,101,0.2)' }} />
                    </Box>
                )}

                {/* Overview Cards */}
                <CampaignOverviewCards campaign={campaign} />

                {/* App Install Token */}
                {campaign.app_install_attribution?.enabled && campaign.app_install_attribution.app_token && (
                    <AppInstallTokenCard
                        appToken={campaign.app_install_attribution.app_token}
                        environment={campaign.app_install_attribution.environment}
                        note={campaign.app_install_attribution.note}
                    />
                )}

                {/* Manage campaign grid */}
                <CampaignManageGrid campaign={campaign} onOpen={setActivePanel} showAddCreators={canUseWallet} />

                {/* Participants */}
                <CampaignParticipants
                    participants={participants}
                    pendingRequests={pendingRequests}
                    pendingInvites={pendingInvites}
                    campaignId={campaign._id}
                    requirements={campaign.requirements}
                />

                {/* Description */}
                {campaign.description && (
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                        <SectionLabel>Campaign Brief</SectionLabel>
                        <Box
                            sx={{
                                '& .rich-text-wrapper .ql-editor': {
                                    color: 'rgba(255,255,255,0.82) !important',
                                    lineHeight: 1.7,
                                    fontSize: 15,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                },
                            }}>
                            <RichTextViewer content={campaign.description} />
                        </Box>
                    </Box>
                )}

                {/* Detail Sections */}
                <CampaignDetailSections campaign={campaign} />
            </Box>

            <CampaignEditModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                campaign={campaign}
                onSaved={() => setEditOpen(false)}
            />

            <CampaignBriefPanel
                open={activePanel === 'brief'}
                onClose={() => setActivePanel(null)}
                campaign={campaign}
            />
            <CampaignDeliverablesPanel
                open={activePanel === 'deliverables'}
                onClose={() => setActivePanel(null)}
                campaign={campaign}
            />
            <CampaignTargetingPanel
                open={activePanel === 'targeting'}
                onClose={() => setActivePanel(null)}
                campaign={campaign}
            />
            <CampaignAssetsPanel
                open={activePanel === 'assets'}
                onClose={() => setActivePanel(null)}
                campaign={campaign}
            />
            <CampaignSampleVideosPanel
                open={activePanel === 'videos'}
                onClose={() => setActivePanel(null)}
                campaign={campaign}
            />
            <CampaignAddCreatorsPanel
                open={activePanel === 'add_creators'}
                onClose={() => setActivePanel(null)}
                campaign={campaign}
                canUseWallet={canUseWallet}
                onFundWallet={() => {
                    setActivePanel(null);
                    navigate(ROUTES.BRAND_WALLET);
                }}
            />
            <Drawer
                anchor="right"
                open={activePanel === 'team'}
                onClose={() => setActivePanel(null)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 'min(50vw, 720px)' },
                        maxWidth: '100%',
                        bgcolor: midnight.surface,
                        backgroundImage: 'none',
                        borderLeft: '1px solid rgba(255,255,255,0.08)',
                        p: 2.5,
                    },
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 800,
                            fontSize: 18,
                            color: midnight.onSurface,
                        }}>
                        Team
                    </Typography>
                    <IconButton
                        onClick={() => setActivePanel(null)}
                        sx={{ color: midnight.onSurface }}
                        aria-label="Close team">
                        <CloseIcon />
                    </IconButton>
                </Box>
                {campaign?._id ? <ResourceTeamPanel resourceType="campaign" resourceId={campaign._id} /> : null}
            </Drawer>

            <CampaignFundModal
                open={fundModalOpen}
                onClose={() => setFundModalOpen(false)}
                campaign={campaign}
                perCampaignFeeCents={perCampaignFeeCents}
                hasActivePlan={hasPlan}
            />

            <BrandCampaignSubscriptionGuard
                open={subGuardOpen}
                flatFeeCents={perCampaignFeeCents}
                context="fund"
                onClose={() => setSubGuardOpen(false)}
                onSubscribe={() => {
                    setSubGuardOpen(false);
                    setTierPickerOpen(true);
                }}
                onContinueWithFee={() => {
                    setSubGuardOpen(false);
                    dispatch(getWalletDashboard());
                    setFundModalOpen(true);
                }}
            />

            <CampaignHostTierPickerModal
                open={tierPickerOpen}
                onClose={() => setTierPickerOpen(false)}
                onSelectMonthly={handleSelectMonthly}
                onSelectLtd={handleSelectLtd}
            />

            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />

            {showDetailTour && (
                <Tutorial stepsDetails={campaignDetailTutorialSteps} configField="show_campaign_detail_guide" />
            )}

            <DuplicateCampaignDialog
                open={duplicateOpen}
                campaign={campaign}
                onClose={() => setDuplicateOpen(false)}
                onDuplicated={newId => navigate(replaceRouteParam(ROUTES.BRAND_CAMPAIGN_DETAIL, 'campaignId', newId))}
            />

            <ConfirmDialog
                open={closeConfirmOpen}
                title="Close this campaign?"
                description="Neither you nor creators will be able to send messages, and creators can no longer submit content. Finish open reviews first. Approved work still waiting on a live link blocks close until the delivery date (or campaign end date) passes. Unused escrow returns to your campaign wallet."
                confirmLabel="Close campaign"
                destructive
                loading={closingCampaign}
                onCancel={() => !closingCampaign && setCloseConfirmOpen(false)}
                onConfirm={async () => {
                    if (!campaignId || !authToken || closingCampaign) return;
                    setClosingCampaign(true);
                    try {
                        const result = await closeUgcCampaign(campaignId, authToken);
                        const released = Number(result?.escrow_released_cents) || 0;
                        showToastMessage(
                            released > 0
                                ? `Campaign closed. $${(released / 100).toFixed(2)} returned to your wallet.`
                                : 'Campaign closed.',
                            'success',
                        );
                        setCloseConfirmOpen(false);
                        dispatch(getOffsiteCampaignDetails({ campaignId, asBrandHost: true }));
                        dispatch(getWalletDashboard());
                    } catch (err: any) {
                        const msg =
                            err?.response?.data?.message ||
                            err?.message ||
                            'Could not close campaign. Finish open submission reviews first.';
                        showToastMessage(msg, 'error');
                    } finally {
                        setClosingCampaign(false);
                    }
                }}
            />
        </MetaTags>
    );
}

/* ── Reusable small pieces ──────────────────────────── */

export function StatusChip({ status }: { status?: string }) {
    const colorMap: Record<string, string> = {
        published: BRAND_LUXE_SECONDARY,
        active: BRAND_LUXE_SECONDARY,
        draft: BRAND_LUXE_PRIMARY,
        completed: '#767575',
        paused: '#ff7351',
        closed: '#767575',
    };
    const color = colorMap[status || ''] || '#a0a0a0';
    return (
        <Chip
            label={status || 'draft'}
            size="small"
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                height: 24,
                textTransform: 'capitalize',
                bgcolor: `${color}18`,
                color,
            }}
        />
    );
}

function AppInstallTokenCard({
    appToken,
    environment,
    note,
}: {
    appToken: string;
    environment: string;
    note?: string;
}) {
    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(appToken);
            showToastMessage('App token copied', 'success');
        } catch {
            showToastMessage('Could not copy token', 'error');
        }
    }, [appToken]);

    return (
        <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
                <SectionLabel>App Install Token</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    {environment === 'sandbox' && (
                        <Chip
                            label="Sandbox"
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                height: 20,
                                bgcolor: 'rgba(255,165,0,0.12)',
                                color: 'orange',
                            }}
                        />
                    )}
                    <Link
                        href={marketingAppInstallDocsUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                            fontSize: 12,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: BRAND_LUXE_PRIMARY,
                            whiteSpace: 'nowrap',
                        }}>
                        Need help?
                    </Link>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: 'rgba(0,0,0,0.25)',
                    border: `1px solid rgba(255,201,101,0.12)`,
                    borderRadius: 1.5,
                    px: 2,
                    py: 1.25,
                }}>
                <Typography
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.85)',
                        flexGrow: 1,
                        wordBreak: 'break-all',
                    }}>
                    {appToken}
                </Typography>
                <Tooltip title="Copy token">
                    <IconButton onClick={handleCopy} size="small" sx={{ color: BRAND_LUXE_PRIMARY, flexShrink: 0 }}>
                        <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            {note && (
                <Typography
                    sx={{
                        fontSize: 12,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: 'rgba(255,255,255,0.4)',
                        mt: 1,
                    }}>
                    {note}
                </Typography>
            )}
        </Box>
    );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontSize: 10,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: BRAND_LUXE_PRIMARY,
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}
